import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // Form data
  formData: {},
  formConfig: null,

  // Auto-save status and notes management
  autoSaveState: {
    isSaving: false,
    lastSaved: null,
    error: null,
  },
  notes: {},
  sectionProgress: {},

  // Notes save status
  notesSaveStatus: {
    isLoading: false,
    error: null,
    savingNote: null,
  },

  // Form submission states
  submission: {
    isSubmitting: false,
    isSubmitted: false,
    submitError: null,
    submittedAt: null,
  },

  // Trader operation states
  traderOperation: {
    isProcessing: false,
    operationType: null, // 'add' or 'remove'
    targetTraderId: null,
  },

  // Form loading
  formLoadStatus: {
    isLoading: false,
    error: null,
  },

  // Section completion tracking
  sectionCompletionStatus: {},

  // Overall form status
  overallCompletion: {
    totalSections: 0,
    completedSections: 0,
    percentComplete: 0,
  },
};

const formSlice = createSlice({
  name: "form",
  initialState,
  reducers: {
    // Form Auto-Save Actions
    formAutoSaveRequest: (state, action) => {
      state.autoSaveState.isSaving = true;
      state.autoSaveState.lastSaved = null;
      state.autoSaveState.error = null;
    },

    formAutoSaveSuccess: (state, action) => {
      const { sectionId, sectionKey, sectionData, completionStatus, savedAt } =
        action.payload;

      state.autoSaveState.isSaving = false;
      state.autoSaveState.lastSaved = savedAt;
      state.autoSaveState.error = null;

      // Update section completion status
      state.sectionCompletionStatus[sectionId] = completionStatus;

      // Update form data
      if (!state.formData[sectionKey]) {
        state.formData[sectionKey] = {};
      }
      state.formData[sectionKey] = { ...sectionData };

      // Recalculate overall completion
      formSlice.caseReducers.calculateOverallCompletion(state);
    },

    formAutoSaveFailure: (state, action) => {
      state.autoSaveState.isSaving = false;
      state.autoSaveState.lastSaved = null;
      state.autoSaveState.error = action.payload.error;
    },

    // Section Note Actions
    formSectionNoteSaveRequest: (state, action) => {
      // Form section note save request
      state.notesSaveStatus.isLoading = true;
      state.notesSaveStatus.savingNote = action.payload.sectionId;
      state.notesSaveStatus.error = null;
    },

    formSectionNoteSaveSuccess: (state, action) => {
      const { sectionId, note, savedAt } = action.payload;
      // Form section note save success

      state.notesSaveStatus.isLoading = false;
      state.notesSaveStatus.savingNote = null;
      state.notesSaveStatus.error = null;

      // Update section notes (use 'notes' instead of 'sectionNotes')
      state.notes[sectionId] = {
        note,
        savedAt,
        status: "submitted_with_note",
      };

      // Updated notes state
    },

    formSectionNoteSaveFailure: (state, action) => {
      // Form section note save failure
      state.notesSaveStatus.isLoading = false;
      state.notesSaveStatus.savingNote = null;
      state.notesSaveStatus.error = action.payload.error;
    },

    // Form Load Actions
    formLoadRequest: (state, action) => {
      state.formLoadStatus.isLoading = true;
      state.formLoadStatus.error = null;
    },

    formLoadSuccess: (state, action) => {
      const { formConfig, formData, sectionNotes } = action.payload;

      state.formLoadStatus.isLoading = false;
      state.formLoadStatus.error = null;
      state.formConfig = formConfig;
      state.formData = formData || {};
      state.notes = sectionNotes || {};

      // Recalculate completion status
      formSlice.caseReducers.calculateOverallCompletion(state);
    },

    formLoadFailure: (state, action) => {
      state.formLoadStatus.isLoading = false;
      state.formLoadStatus.error = action.payload.error;
    },

    // Local form data update (for immediate UI updates)
    updateFormData: (state, action) => {
      const { sectionKey, fieldId, value } = action.payload;

      if (!state.formData[sectionKey]) {
        state.formData[sectionKey] = {};
      }
      state.formData[sectionKey][fieldId] = value;
    },

    // Calculate overall completion (helper reducer)
    calculateOverallCompletion: (state) => {
      const completionStatuses = Object.values(state.sectionCompletionStatus);
      const totalSections = completionStatuses.length;
      const completedSections = completionStatuses.filter(
        (status) => status.isComplete || state.notes[status.sectionId]
      ).length;

      state.overallCompletion = {
        totalSections,
        completedSections,
        percentComplete:
          totalSections > 0
            ? Math.round((completedSections / totalSections) * 100)
            : 0,
      };
    },

    // Reset form state
    resetForm: (state) => {
      return { ...initialState };
    },

    // Clear auto-save error
    clearAutoSaveError: (state) => {
      state.autoSaveState.error = null;
    },

    // Clear notes save error
    clearNotesSaveError: (state) => {
      state.notesSaveStatus.error = null;
    },

    // Form Submission Actions
    formDataSubmit: (state, action) => {
      // Form data submit
      state.submission.isSubmitting = true;
      state.submission.submitError = null;
      state.submission.isSubmitted = false;
      
      // Check if this is a trader operation
      const payload = action.payload;
      let formData = null;
      
      // Check both possible structures
      if (payload?.payload?.formData) {
        // Trader operation structure: { formId, payload: { formData: {...} } }
        formData = payload.payload.formData;
      } else if (payload?.formData) {
        // Main form submission structure: { formId, payload: { formData: {...} } }
        formData = payload.formData;
      }
      
      if (formData) {
        const formDataKeys = Object.keys(formData);
        const traderSection = formDataKeys.find(key => 
          formData[key]?.isTraderSection === true
        );
        if (traderSection) {
          state.traderOperation.isProcessing = true;
          state.traderOperation.operationType = 'add';
          state.traderOperation.targetTraderId = traderSection;
        }
      }
    },

    formDataSubmitSuccess: (state, action) => {
      // Form data submit success
      state.submission.isSubmitting = false;
      state.submission.isSubmitted = true;
      state.submission.submitError = null;
      state.submission.submittedAt =
        action.payload.submittedAt || new Date().toISOString();
      // Keep trader operation processing until form data is updated
    },

    formDataSubmitFailure: (state, action) => {
      // Form data submit failure
      state.submission.isSubmitting = false;
      state.submission.isSubmitted = false;
      state.submission.submitError = action.payload.error;
      // Clear trader operation on failure
      state.traderOperation.isProcessing = false;
      state.traderOperation.operationType = null;
      state.traderOperation.targetTraderId = null;
    },

    // Add Trader Section Actions
    addTraderSectionRequest: (state, action) => {
      state.traderOperation.isProcessing = true;
      state.traderOperation.operationType = 'add';
      state.traderOperation.targetTraderId = null;
    },

    addTraderSectionSuccess: (state, action) => {
      state.traderOperation.isProcessing = false;
      state.traderOperation.operationType = null;
      state.traderOperation.targetTraderId = null;
    },

    addTraderSectionFailure: (state, action) => {
      state.traderOperation.isProcessing = false;
      state.traderOperation.operationType = null;
      state.traderOperation.targetTraderId = null;
    },

    // Delete Trader Section Actions
    deleteTraderSectionRequest: (state, action) => {
      state.submission.isSubmitting = true;
      state.submission.submitError = null;
      state.traderOperation.isProcessing = true;
      state.traderOperation.operationType = 'remove';
      state.traderOperation.targetTraderId = action.payload.traderId;
    },

    deleteTraderSectionSuccess: (state, action) => {
      state.submission.isSubmitting = false;
      // Keep trader operation processing until form data is updated
      // This will be cleared when the form data is actually updated
    },

    deleteTraderSectionFailure: (state, action) => {
      state.submission.isSubmitting = false;
      state.submission.submitError = action.payload.error;
      state.traderOperation.isProcessing = false;
      state.traderOperation.operationType = null;
      state.traderOperation.targetTraderId = null;
    },

    // Clear trader operation state when form data is updated
    clearTraderOperation: (state) => {
      state.traderOperation.isProcessing = false;
      state.traderOperation.operationType = null;
      state.traderOperation.targetTraderId = null;
    },

    // Delete File Actions
    deleteFileRequest: (state, action) => {
      state.submission.isSubmitting = true;
      state.submission.submitError = null;
    },

    deleteFileSuccess: (state, action) => {
      state.submission.isSubmitting = false;
      // Update form data with the updated form (file removed)
      state.formData = action.payload;
    },

    deleteFileFailure: (state, action) => {
      state.submission.isSubmitting = false;
      state.submission.submitError = action.payload.error;
    },
  },
});

// Export actions
export const {
  formAutoSaveRequest,
  formAutoSaveSuccess,
  formAutoSaveFailure,
  formSectionNoteSaveRequest,
  formSectionNoteSaveSuccess,
  formSectionNoteSaveFailure,
  formLoadRequest,
  formLoadSuccess,
  formLoadFailure,
  updateFormData,
  calculateOverallCompletion,
  resetForm,
  clearAutoSaveError,
  clearNotesSaveError,
  formDataSubmit,
  formDataSubmitSuccess,
  formDataSubmitFailure,
  addTraderSectionRequest,
  addTraderSectionSuccess,
  addTraderSectionFailure,
  deleteTraderSectionRequest,
  deleteTraderSectionSuccess,
  deleteTraderSectionFailure,
  clearTraderOperation,
  deleteFileRequest,
  deleteFileSuccess,
  deleteFileFailure,
} = formSlice.actions;

// Export reducer as default
export default formSlice.reducer;
